export const GA_MEASUREMENT_ID = "G-41MECLPDHZ"

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

function findGtagScript(): HTMLScriptElement | null {
  return document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)
}

function currentPagePath(): string {
  if (typeof window === "undefined") {
    return "/"
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function configPayload(pagePath?: string): Record<string, string | boolean> {
  const path = pagePath ?? currentPagePath()
  const payload: Record<string, string | boolean> = {
    page_path: path,
    send_page_view: true,
  }
  if (process.env.GATSBY_GA_DEBUG === "true") {
    payload.debug_mode = true
  }
  return payload
}

export function isGoogleAnalyticsLoaded(): boolean {
  return typeof window !== "undefined" && Boolean(findGtagScript())
}

let lastPageViewSent: { path: string; at: number } | null = null
const PAGE_VIEW_DEDUP_MS = 2500

/**
 * Record a page view on client-side navigation (Gatsby SPA). No-op if gtag is not ready.
 * Deduplicates the same path within a short window (avoids double hits from bootstrap + onRouteUpdate).
 */
export function sendGa4PageView(pagePath: string): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return
  }
  const now = Date.now()
  if (
    lastPageViewSent !== null &&
    lastPageViewSent.path === pagePath &&
    now - lastPageViewSent.at < PAGE_VIEW_DEDUP_MS
  ) {
    return
  }
  lastPageViewSent = { path: pagePath, at: now }
  window.gtag("config", GA_MEASUREMENT_ID, configPayload(pagePath))
}

/**
 * Injects gtag.js and sends initial config. Call only after explicit analytics consent.
 */
export function loadGoogleAnalytics(): void {
  if (typeof window === "undefined" || findGtagScript()) {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args)
  }

  const script = document.createElement("script")
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(script)

  window.gtag("js", new Date())
  sendGa4PageView(currentPagePath())
}
